import {action, computed, observable, toJS} from 'mobx';
import $ from 'jquery';
import Cookie from 'js-cookie';

import {STATE} from 'lib/struct/page/SaveButton';

import PluginSettings from 'domain/model/PluginSettings';
import Theme from 'domain/model/wa/Theme';
import Feature from 'domain/model/wa/Feature';
import GENERAL_STOREFRONT from 'domain/constant/GeneralStorefront';

import SettingsPageSettings from 'settings/model/SettingsPageSettings';
import TemplateMeta from 'settings/model/TemplateMeta';
import MarkupTemplateThemeSettings from 'settings/model/MarkupTemplateThemeSettings';
import MarkupStyleSettings from 'settings/model/MarkupStyleSettings';
import BaseStyleSettings from 'settings/model/BaseStyleSettings';
import * as MarkupTemplateId from 'settings/constant/MarkupTemplateId';

export default class SettingsContext {
	/** @type {SettingsPageSettings} */
	@observable settings;

	@observable save_state = STATE.None;
	@observable markup_template_loading_state = STATE.None;
	save_state_timeout_id = 0;


	@observable current_storefront = GENERAL_STOREFRONT;
	/** @type {Group} */
	@observable edited_group = null;


	_storefront_options = [{
		label: 'Все витрины',
		value: GENERAL_STOREFRONT,
	}];

	/** @type {Array<Feature>} */
	color_features;
	color_feature_options;
	image_size_options;

	/** @type {Map<string, Theme>} */
	themes;
	/** @type {Map<String, TemplateMeta>} */
	templates_meta;
	@observable current_theme_id;
	@observable current_markup_template_id = MarkupTemplateId.GROUPS_BLOCK;


	@observable current_style_theme_id;
	@observable current_style_storefront = GENERAL_STOREFRONT;
	@observable markup_styles_loading_state = STATE.None;


	group_helper_template;
	category_group_helper_template;

	@observable is_copy_to_clipboard_available;

	constructor(state) {
		if (!state) {
			throw 'Context [state] is required';
		}

		const {storefronts, themes, templates_meta, color_features, image_sizes, group_helper_template, category_group_helper_template} = state;

		storefronts.forEach(storefront => {
			this._storefront_options.push({
				label: storefront,
				value: storefront,
			});
		});

		this.themes = new Map(themes.map(theme => ([
			theme.id,
			new Theme(theme)
		])));

		this.templates_meta = new Map(templates_meta.map(meta => ([
			meta.template_id,
			new TemplateMeta(meta)
		])));

		this.color_features = color_features.map(f => new Feature(f));
		this.color_feature_options = this.color_features.map(feature => ({
			label: feature.name,
			value: feature.id,
		}));
		this.color_feature_options.unshift({
			label: '',
			value: '0',
		});

		this.image_size_options = image_sizes.map(size => ({
			label: getImageSizeLabel(size),
			value: size,
		}));

		this.image_size_options.unshift({
			label: '',
			value: '',
		});

		this.group_helper_template = group_helper_template;
		this.category_group_helper_template = category_group_helper_template;

		this.is_copy_to_clipboard_available = !(window.localStorage && localStorage.productgroup_backend_is_copy_to_clipboard_available === '0');

		this._createSettingsByState(state);
	}

	get group_template_options() {
		return [{
			label: 'Список',
			value: MarkupTemplateId.SIMPLE_GROUP,
		},{
			label: 'Фото',
			value: MarkupTemplateId.PHOTO_GROUP,
		},{
			label: 'Цвет',
			value: MarkupTemplateId.COLOR_GROUP,
		},];
	}

	@computed get storefront_options() {
		const getOptionStyle = (option) => {
			const style = {};

			if (this.settings.storefronts_with_personal_settings.has(option.value)) {
				style.color = 'red';
			}

			return style;
		};

		return this._storefront_options.map(option => ({
			...option,
			style: getOptionStyle(option),
		}));
	}

	@computed get theme_options() {
		const with_personal = this.settings.theme_ids_with_personal_settings;

		/**
		 * @param {Theme} theme
		 */
		const getOptionStyle = (theme) => {
			const style = {};

			if (with_personal.has(theme.id)) {
				style.color = 'red';
			}

			return style;
		};

		return Array.from(this.themes.values()).map(theme => ({
			label: theme.name,
			value: theme.id,
			style: getOptionStyle(theme),
		}));
	}

	@computed get style_theme_options() {
		const with_personal = this.settings.theme_storefronts_with_personal_style_settings;

		/**
		 * @param {Theme} theme
		 */
		const getOptionStyle = (theme) => {
			const style = {};

			if (
				with_personal.has(theme.id)
				&& Array.from(with_personal.get(theme.id).keys()).filter(s => s !== GENERAL_STOREFRONT).length > 0
			) {
				style.color = 'red'
			}

			if (Array.from(theme.storefronts.keys()).length > 0) {
				style.fontWeight = 'bold';
			}

			return style;
		};

		return Array.from(this.themes.values()).map(theme => ({
			label: theme.name,
			value: theme.id,
			style: getOptionStyle(theme),
		}));
	}

	@computed get style_theme_storefront_options() {
		const theme_id = this.current_style_theme_id;
		const theme = this.themes.get(theme_id);
		const with_personal = this.settings.theme_storefronts_with_personal_style_settings;

		const getOptionStyle = (option) => {
			const style = {};

			if (
				option.value !== GENERAL_STOREFRONT
				&& with_personal.has(theme_id)
				&& with_personal.get(theme_id).has(option.value)
			) {
				style.color = 'red'
			}

			return style;
		};

		return this._storefront_options
			.filter(option => option.value === GENERAL_STOREFRONT || theme.storefronts.has(option.value))
			.map(option => ({
				...option,
				style: getOptionStyle(option)
			}));
	}

	getSpecificProductGroupHelper(group_id) {
		return group_id > 0
			? this.group_helper_template.replace('%GROUP_ID%', group_id)
			: '';
	}

	getSpecificCategoryGroupHelper(group_id) {
		return group_id > 0
			? this.category_group_helper_template.replace('%GROUP_ID%', group_id)
			: '';
	}

	/** @return {PluginSettings} */
	getGeneralStorefrontPluginSettings() {
		return this.settings.storefront_plugin_settings.get(GENERAL_STOREFRONT);
	}

	/**
	 * @param theme_id
	 * @return {MarkupStyleSettings}
	 */
	getGeneralMarkupStyleSettings(theme_id) {
		return this.settings.markup_style_theme_storefront_settings
			.get(theme_id)
			.get(GENERAL_STOREFRONT);
	}

	@computed get markup_templates_is_in_loading() {
		return this.markup_template_loading_state === STATE.Loading;
	}

	/**
	 * @return {SettingsContext|null}
	 */
	@computed get current_plugin_settings() {
		return this.settings.storefront_plugin_settings.has(this.current_storefront)
			? this.settings.storefront_plugin_settings.get(this.current_storefront)
			: null;
	}

	@computed get markup_styles_is_in_loading() {
		return this.markup_styles_loading_state === STATE.Loading;
	}

	/**
	 * @return {MarkupStyleSettings|null}
	 */
	@computed get current_markup_style_settings() {
		try {
			return this.settings.markup_style_theme_storefront_settings
				.get(this.current_style_theme_id)
				.get(this.current_style_storefront);
		}
		catch (e) {
			return null;
		}
	}

	isCurrentStorefrontWithPersonal() {
		return this.settings.storefronts_with_personal_settings.has(this.current_storefront);
	}

	@action updateCurrentPluginSettingsValue(name, value) {
		const current_storefront = this.current_storefront;

		if (this.current_plugin_settings[name] === value) {
			return;
		}

		if (current_storefront !== GENERAL_STOREFRONT) {
			if (!this.settings.storefronts_with_personal_settings.has(current_storefront)) {
				const general_settings = this.getGeneralStorefrontPluginSettings();
				const new_settings = new PluginSettings(general_settings);

				this.settings.storefront_plugin_settings.set(current_storefront, new_settings);
			}

			this.settings.storefronts_with_personal_settings.set(current_storefront, true);

			this.settings.storefronts_to_clear.delete(current_storefront);
		}

		this.current_plugin_settings[name] = value;
	}

	/**
	 * @param {Group} group
	 */
	@action openGroupSettingsPopup(group) {
		this.edited_group = group;
	}

	@action closeGroupSettingsPopup() {
		this.edited_group = null;
	}

	@action async changeCurrentStorefront(storefront) {
		this.current_storefront = storefront;

		if (storefront !== GENERAL_STOREFRONT && !this.settings.storefront_plugin_settings.has(storefront)) {
			const settings = await this._fetchStorefrontPluginSettings(storefront);

			if (settings) {
				this.settings.storefront_plugin_settings.set(storefront, settings);
				this.settings.storefronts_with_personal_settings.set(storefront, true);
			} else {
				this.settings.storefront_plugin_settings.set(storefront, this.getGeneralStorefrontPluginSettings());
				this.settings.storefronts_with_personal_settings.delete(storefront);
				this.settings.storefronts_to_clear.delete(storefront);
			}
		}
	}

	@action resetPluginSettingsForCurrentStorefront() {
		const current_storefront = this.current_storefront;

		if (current_storefront !== GENERAL_STOREFRONT) {
			this.settings.storefronts_to_clear.set(current_storefront, true);

			this.settings.storefronts_with_personal_settings.delete(current_storefront);

			// const general_copy_settings = PluginStorefrontSettings(this.getGeneralPluginStorefrontSettings());
			const general_copy_settings = this.getGeneralStorefrontPluginSettings();
			this.settings.storefront_plugin_settings.set(current_storefront, general_copy_settings);
		}
	}

	@action async updateCurrentTheme(current_theme_id) {
		this.current_theme_id = this.themes.get(current_theme_id).id;
		this.current_markup_template_id = MarkupTemplateId.GROUPS_BLOCK;

		if (!this.settings.markup_template_theme_settings.has(current_theme_id)) {
			const {markup_template_settings, base_styles_settings} = {...await this._fetchMarkupTemplateThemeSettings(current_theme_id)};

			this.settings.loaded_theme_ids.set(current_theme_id, true);

			if (markup_template_settings) {
				this.settings.markup_template_theme_settings.set(current_theme_id, markup_template_settings);
				this.settings.base_style_theme_settings.set(current_theme_id, base_styles_settings);
			} else {
				console.warn(`Ошибка: не получилось загрузить настройки smarty шаблонов для витрины ${current_theme_id}`);

				this.settings.theme_ids_with_personal_settings.delete(current_theme_id);
			}
		}
	}

	@action updateCurrentThemeMarkupTemplateSettingsValue(name, value) {
		const current_theme_settings = this.settings.markup_template_theme_settings.get(this.current_theme_id);
		if (!current_theme_settings) {
			return;
		}

		if (current_theme_settings[name] === value) {
			return;
		}

		current_theme_settings[name] = value;

		if (current_theme_settings.is_default) {
			this.settings.theme_ids_with_personal_settings.delete(this.current_theme_id);
		} else {
			this.settings.theme_ids_with_personal_settings.set(this.current_theme_id, true);
		}
	}


	@action updateCurrentThemeBaseStyleSettingsValue(name, value) {
		const theme_id = this.current_theme_id;
		const current_settings = this.settings.base_style_theme_settings.get(theme_id);

		if (current_settings[name] === value) {
			return;
		}

		current_settings[name] = value;

		if (current_settings.is_default) {
			this.settings.theme_ids_with_personal_settings.delete(this.current_theme_id);
		} else {
			this.settings.theme_ids_with_personal_settings.set(this.current_theme_id, true);
		}
	}


	@action async updateCurrentStyleTheme(theme_id) {
		this.current_style_theme_id = theme_id;

		await this.updateCurrentStyleThemeStorefront(GENERAL_STOREFRONT);
	}

	@action async updateCurrentStyleThemeStorefront(storefront) {
		const theme_id = this.current_style_theme_id;
		this.current_style_storefront = storefront;

		if (!this.settings.markup_style_theme_storefront_settings.has(theme_id)) {
			this.settings.markup_style_theme_storefront_settings.set(theme_id, new Map());
		}

		const storefronts_settings = this.settings.markup_style_theme_storefront_settings.get(theme_id);

		if (!storefronts_settings.has(GENERAL_STOREFRONT)) {
			const general_settings = await this._fetchThemeStorefrontMarkupStyleSettings(theme_id, GENERAL_STOREFRONT);

			if (!general_settings) {
				storefronts_settings.set(GENERAL_STOREFRONT, null);

				throw 'Не удалось загрузить настройки стилей для всех витрин';
			}

			storefronts_settings.set(GENERAL_STOREFRONT, general_settings);
		}

		if (storefronts_settings.has(storefront)) {
			return;
		}

		if (storefront !== GENERAL_STOREFRONT) {
			const settings = await this._fetchThemeStorefrontMarkupStyleSettings(theme_id, storefront);

			if (!this.settings.theme_storefronts_with_personal_style_settings.has(theme_id)) {
				this.settings.theme_storefronts_with_personal_style_settings.set(theme_id, new Map());
			}
			if (!this.settings.theme_storefront_personal_style_settings_to_clear.has(theme_id)) {
				this.settings.theme_storefront_personal_style_settings_to_clear.set(theme_id, new Map());
			}

			if (settings) {
				this.settings.markup_style_theme_storefront_settings
					.get(theme_id)
					.set(storefront, settings);

				this.settings.theme_storefronts_with_personal_style_settings
					.get(theme_id)
					.set(storefront, true);
			} else {
				this.settings.markup_style_theme_storefront_settings
					.get(theme_id)
					.set(storefront, storefronts_settings.get(GENERAL_STOREFRONT));

				this.settings.theme_storefronts_with_personal_style_settings
					.get(theme_id)
					.delete(storefront);
				this.settings.theme_storefront_personal_style_settings_to_clear
					.get(theme_id)
					.delete(storefront);
			}
		}
	}

	@action updateCurrentMarkupStyleSettingsValue(name, value) {
		const theme_id = this.current_style_theme_id;
		const storefront = this.current_style_storefront;

		const current_value = this.current_markup_style_settings[name];
		if (current_value === value) {
			return;
		}

		if (storefront !== GENERAL_STOREFRONT) {
			const with_personal = this.settings.theme_storefronts_with_personal_style_settings;
			if (!with_personal.has(theme_id)) {
				with_personal.set(theme_id, new Map());
			}

			if (!with_personal.get(theme_id).has(storefront)) {
				const general_settings = this.getGeneralMarkupStyleSettings(theme_id);
				const new_settings = new MarkupStyleSettings(general_settings);

				this.settings.markup_style_theme_storefront_settings
					.get(theme_id)
					.set(storefront, new_settings);
			}

			with_personal.get(theme_id).set(storefront, true);

			if (this.settings.theme_storefront_personal_style_settings_to_clear.has(theme_id)) {
				this.settings.theme_storefront_personal_style_settings_to_clear.get(theme_id).delete(storefront);
			}
		}

		const settings = this.settings.markup_style_theme_storefront_settings
			.get(theme_id)
			.get(storefront);

		if (settings) {
			this.current_markup_style_settings[name] = value;
		}
	}

	isCurrentMarkupStyleStorefrontWithPersonal() {
		const with_personal = this.settings.theme_storefronts_with_personal_style_settings;

		return this.current_style_storefront !== GENERAL_STOREFRONT
			&& with_personal.has(this.current_style_theme_id)
			&& with_personal.get(this.current_style_theme_id).has(this.current_style_storefront);
	}

	@action resetCurrentMarkupStyleSettings() {
		const theme_id = this.current_style_theme_id;
		const storefront = this.current_style_storefront;

		if (storefront === GENERAL_STOREFRONT) {
			return;
		}

		if (!this.settings.theme_storefront_personal_style_settings_to_clear.has(theme_id)) {
			this.settings.theme_storefront_personal_style_settings_to_clear.set(theme_id, new Map());
		}

		const general_settings = this.getGeneralMarkupStyleSettings(theme_id);
		this.settings.markup_style_theme_storefront_settings
			.get(theme_id)
			.set(storefront, general_settings);

		this.settings.theme_storefront_personal_style_settings_to_clear.get(theme_id).set(storefront, true);

		if (this.settings.theme_storefronts_with_personal_style_settings.has(theme_id)) {
			this.settings.theme_storefronts_with_personal_style_settings.get(theme_id).delete(storefront)
		}
	}


	@action disableCopyToClipboard() {
		if (!window.localStorage) {
			return;
		}

		localStorage.setItem('productgroup_backend_is_copy_to_clipboard_available', '0');
	}

	@action async saveSettings() {
		if (this.save_state_timeout_id) {
			clearTimeout(this.save_state_timeout_id);

			this.save_state_timeout_id = 0;
		}

		this.save_state = STATE.Loading;

		const data = {
			settings: JSON.stringify(toJS(this.settings)),
			_csrf: Cookie.get('_csrf'),
		};

		try {
			const response = await $.ajax({
				url: '?plugin=productgroup&module=settings&action=save',
				method: 'POST',
				data: data,
			});

			const success = response && response.data && response.data.state;

			if (success) {
				this.save_state = STATE.Success;

				this.settings.storefronts_to_clear = new Map();

				this._updateGroups(response.data.state);
			} else {
				this.save_state = STATE.Error;
			}
		}
		catch (e) {
			this.save_state = STATE.Error;
		}

		this.save_state_timeout_id = setTimeout(() => {
			this.save_state_timeout_id = null;

			this.save_state = STATE.None;
		}, 1500);
	}

	@action _createSettingsByState(state) {
		const {
			storefront_plugin_settings, storefronts_with_personal_settings,
			groups,
			markup_template_theme_settings, theme_ids_with_personal_settings,
			base_style_theme_settings,
			markup_style_theme_storefront_settings, theme_storefronts_with_personal_style_settings
		} = state;

		this.settings = new SettingsPageSettings(
			storefront_plugin_settings, storefronts_with_personal_settings,
			groups,
			markup_template_theme_settings, theme_ids_with_personal_settings,
			base_style_theme_settings,
			markup_style_theme_storefront_settings, theme_storefronts_with_personal_style_settings
		);

		const theme_ids = Array.from(this.settings.markup_template_theme_settings.keys());
		const current_theme_id = theme_ids.length > 0 ? theme_ids[0] : null;

		this.current_theme_id = current_theme_id;
		this.current_style_theme_id = current_theme_id;
	}

	@action _updateGroups(state) {
		const {groups} = state;

		this.settings.updateGroups(groups);
	}

	@action async _fetchMarkupTemplateThemeSettings(theme_id) {
		this.markup_template_loading_state = STATE.Loading;

		try {
			const response = await $.ajax({
				url: '?plugin=productgroup&module=settings&action=getThemeSettings',
				method: 'GET',
				dataType: 'json',
				data: {
					theme_id: theme_id,
				},
			});

			if (response && !response.errors && response.data && response.data.success) {
				const markup_template_settings = new MarkupTemplateThemeSettings(response.data.state.markup_template_settings);
				const base_styles_settings = new BaseStyleSettings(response.data.state.base_styles_settings);

				this.resetMarkupTemplateLoadingState(STATE.Success);

				return {
					markup_template_settings: markup_template_settings,
					base_styles_settings: base_styles_settings
				};
			} else {
				this.resetMarkupTemplateLoadingState(STATE.Error);

				return null;
			}
		}
		catch (e) {
			this.resetMarkupTemplateLoadingState(STATE.Error);

			return null;
		}
	}

	/**
	 *
	 * @param {String} storefront
	 * @return {Promise<PluginSettings|null>}
	 * @private
	 */
	async _fetchStorefrontPluginSettings(storefront) {
		try {
			const response = await $.ajax({
				url: '?plugin=productgroup&module=storefrontPluginSettings&action=getSettings',
				method: 'GET',
				dataType: 'json',
				data: {
					storefront: storefront,
				},
			});

			if (response && !response.errors && response.data && response.data.success) {
				return response.data.state.have_settings
					? new PluginSettings(response.data.state.settings)
					: null;
			} else {
				return null;
			}
		}
		catch (e) {
			return null;
		}
	}

	/**
	 * @param theme_id
	 * @param storefront
	 * @return {Promise<MarkupStyleSettings|null>}
	 * @private
	 */
	@action async _fetchThemeStorefrontMarkupStyleSettings(theme_id, storefront) {
		try {
			const response = await $.ajax({
				url: '?plugin=productgroup&module=themeStorefrontMarkupStyle&action=getSettings',
				method: 'GET',
				dataType: 'json',
				data: {
					theme_id: theme_id,
					storefront: storefront,
				},
			});

			if (response && !response.errors && response.data && response.data.success) {
				return storefront === GENERAL_STOREFRONT || response.data.state.have_settings
					? new MarkupStyleSettings(response.data.state.settings)
					: null;
			} else {
				return null;
			}
		}
		catch (e) {
			return null;
		}
	}

	@action resetMarkupTemplateLoadingState(state) {
		this.markup_template_loading_state = state;

		setTimeout(() => {
			this.markup_template_loading_state = STATE.None;
		}, 1500);
	}
}

const getImageSizeLabel = size => {
	const size_parts = size.split('x');

	if (size_parts.length === 1) {
		return `Макс. ( Ширина, Высота ) = ${size} px`;
	} else if (size_parts.length === 2) {
		let width = size_parts[0];
		let height = size_parts[1];

		if (width === height) {
			return `Квадратная обрезка: ${width}x${height} px`;
		}

		width = width === ''
			? 'авто'
			: `${width} px`;

		height = height === ''
			? 'авто'
			: `${height} px`;

		return `Ширина = ${width}, Высота = ${height}`;

	} else {
		return '';
	}
};